import { createContext, useContext, useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type UserRole = "master" | "professor" | "owner" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: UserRole;
  tenantId: string | null;
  isStaff: boolean;
  isPlatformOwner: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  role: null,
  tenantId: null,
  isStaff: false,
  isPlatformOwner: false,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isStaff, setIsStaff] = useState(false);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const fetchingRef = useRef(false);

  const fetchRole = async (userId: string) => {
    try {
      const { data: member, error: memberErr } = await supabase
        .from("tenant_members")
        .select("role, tenant_id")
        .eq("user_id", userId)
        .eq("status", "active")
        .maybeSingle();

      if (memberErr) {
        console.warn("[AuthContext] tenant_members query error:", memberErr.message);
      }

      if (member?.role) {
        setRole(member.role as UserRole);
        setTenantId(member.tenant_id);
        setIsStaff(true);
        setIsPlatformOwner(false);
        return;
      }

      const { data: owner, error: ownerErr } = await supabase
        .from("platform_owners")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (ownerErr) {
        console.warn("[AuthContext] platform_owners query error:", ownerErr.message);
      }

      if (owner) {
        setRole("owner");
        setTenantId(null);
        setIsStaff(true);
        setIsPlatformOwner(true);
        return;
      }

      setRole(null);
      setTenantId(null);
      setIsStaff(false);
      setIsPlatformOwner(false);
    } catch (err) {
      console.error("[AuthContext] fetchRole exception:", err);
    }
  };

  useEffect(() => {
    // loading starts as true — only set to false after role resolution

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Always set loading true when we get a new user, to block redirects until role resolves
        setLoading(true);

        // Guard against concurrent fetchRole calls
        if (!fetchingRef.current) {
          fetchingRef.current = true;
          setTimeout(() => {
            fetchRole(session.user.id).finally(() => {
              fetchingRef.current = false;
              setLoading(false);
            });
          }, 0);
        }
      } else {
        fetchingRef.current = false;
        setRole(null);
        setTenantId(null);
        setIsStaff(false);
        setIsPlatformOwner(false);
        setLoading(false);
      }
    });

    // getSession is no longer needed — onAuthStateChange fires INITIAL_SESSION
    // which covers the initial load. This avoids double fetchRole entirely.

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setRole(null);
    setTenantId(null);
    setIsStaff(false);
    setIsPlatformOwner(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, role, tenantId, isStaff, isPlatformOwner, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
